import { formatInTimeZone } from "date-fns-tz";
import { isActivityAvailabilityConfigured } from "./ActivityAvailability.js";
import {
  PHOTO_COMPETITION_ACTIVITY_ID,
  PHOTO_SUBMISSION_ACTIVITY_ID,
} from "./ActivityPhotoForm.js";
import { PARTICIPANT_VOTING_ACTIVITY_ID } from "./ActivityVotingForm.js";

const ACTIVITY_TYPE_LABELS = {
  1: "Quiz",
  [PHOTO_SUBMISSION_ACTIVITY_ID]: "Envio de foto",
  [PHOTO_COMPETITION_ACTIVITY_ID]: "Competição de fotos",
  [PARTICIPANT_VOTING_ACTIVITY_ID]: "Votação",
};

const formatAvailabilityDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return formatInTimeZone(date, "America/Sao_Paulo", "dd/MM/yyyy HH:mm");
};

export const getActivityScheduledText = (activity = {}, now = new Date()) => {
  const availability = activity?.json?.availability || {};
  if (!isActivityAvailabilityConfigured(availability.status) || !availability.start_at) return "";

  const start = new Date(availability.start_at);
  if (Number.isNaN(start.getTime()) || now.getTime() >= start.getTime()) return "";

  const formattedStart = formatAvailabilityDate(availability.start_at);
  return formattedStart ? `Agendado para ${formattedStart}` : "";
};

export const getActivityTypeLabel = (appId) => ACTIVITY_TYPE_LABELS[Number(appId)] || "Atividade";

export const getActivityDisplayName = (activity = {}) => {
  const activityName = String(activity?.activity_name || "").trim();
  if (activityName && !/[?]|Ã|Â|�/.test(activityName)) return activityName;
  return getActivityTypeLabel(activity?.app_id);
};

export const getActivityStatusText = (status, activity = {}, now = new Date()) => {
  const scheduledText = getActivityScheduledText(activity, now);
  if (scheduledText) return scheduledText;

  const normalizedStatus = Number(status ?? 0);
  if (normalizedStatus === 1) return "Em andamento";
  if (normalizedStatus === 2) return "Encerrada";
  return "Não iniciado";
};

export const getActivityStatusBadgeClass = (status, activity = {}, now = new Date()) => {
  if (getActivityScheduledText(activity, now)) return "bg-secondary";

  const normalizedStatus = Number(status ?? 0);
  if (normalizedStatus === 1) return "bg-primary";
  if (normalizedStatus === 2) return "bg-danger";
  return "bg-secondary";
};

export const getActivityAvailabilityText = (activity = {}) => {
  const availability = activity?.json?.availability || {};
  if (!isActivityAvailabilityConfigured(availability.status)) return "";

  const start = formatAvailabilityDate(availability.start_at);
  const end = formatAvailabilityDate(availability.end_at);
  if (start && end) return `${start} até ${end}`;
  if (start) return `A partir de ${start}`;
  if (end) return `Até ${end}`;
  return "Período habilitado";
};

export const normalizeActivityScoreJson = (json = {}, appId) => {
  const normalized = { ...json };
  const numericAppId = Number(appId);
  const key = numericAppId === PHOTO_SUBMISSION_ACTIVITY_ID
    ? "score_id"
    : [PHOTO_COMPETITION_ACTIVITY_ID, PARTICIPANT_VOTING_ACTIVITY_ID].includes(numericAppId)
      ? "winner_score_id"
      : null;
  if (key) {
    const scoreId = Number(normalized[key]);
    normalized[key] = Number.isInteger(scoreId) && scoreId > 0 ? scoreId : null;
  }
  return normalized;
};

export const getQuizPossibleScore = (activity = {}) => (
  (Array.isArray(activity?.json?.questions) ? activity.json.questions : [])
    .filter((question) => Number(question?.exercise ?? 1) === 1)
    .reduce((total, question) => {
      const value = Number(question?.value);
      return total + (Number.isFinite(value) && value > 0 ? value : 0);
    }, 0)
);

export const getActivityScoreText = (activity = {}, availableScores = []) => {
  const appId = Number(activity.app_id);
  if (appId === 1) return `${getQuizPossibleScore(activity)} pontos`;

  const type = appId === PHOTO_SUBMISSION_ACTIVITY_ID
    ? "activity_photo_approval"
    : appId === PHOTO_COMPETITION_ACTIVITY_ID
      ? "activity_photo_competition_winner"
      : appId === PARTICIPANT_VOTING_ACTIVITY_ID
        ? "activity_participant_voting_winner"
        : null;
  if (!type) return "Regra de pontuação não encontrada";

  const scoreId = appId === PHOTO_SUBMISSION_ACTIVITY_ID
    ? activity?.json?.score_id
    : activity?.json?.winner_score_id;
  const compatibleScores = (availableScores || []).filter((option) => option.type === type);
  const hasScoreId = scoreId !== null && scoreId !== undefined && scoreId !== "";
  const score = hasScoreId
    ? compatibleScores.find((option) => Number(option.id) === Number(scoreId))
    : compatibleScores.find((option) => option.is_custom !== undefined && Number(option.is_custom) === 0)
      || compatibleScores.find((option) => option.is_custom === undefined || option.is_custom === null)
      || compatibleScores[0];
  if (!score) return "Regra de pontuação não encontrada";

  const isCustomScore = score.is_custom !== undefined
    && score.is_custom !== null
    && Number(score.is_custom) === 1;
  const scoreLabel = isCustomScore ? (score.description || `Regra #${score.id}`) : "Padrão";
  return `${scoreLabel} (${Number(score.value || 0)} pontos)`;
};
